import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  ButtonGroup,
  ButtonIcon,
  Icon,
  Tooltip,
  Loader,
  Typography,
} from '@statsbomb/kitbag-components';
import { useQuery, useReactiveVar } from '@apollo/client';
import {
  AgGridReact,
  ColumnAutoSizeModule,
  ModuleRegistry,
  SparklinesModule,
  themeQuartz,
} from '@hudl/ag-grid';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { mf_Games, mf_Teams } from '../../../apollo';
import { useDistinctTeamColors } from '../../../utils/hooks/useDistinctTeamColors';
import { GET_GAME_PLAYS } from '../GamePlayByPlay/GetGamePlays';
import PlaysGrid from './PlaysGrid';
import TeamIcon from './TeamIcon';
import PlayDescriptionCell from './PlayDescriptionCell';
import { formatPlayClock, resolvePenaltyText } from './PlayDescription';
import {
  formatPlay,
  formatDriveInfo,
} from '../GamePlayByPlay/PlayByPlay.DataManipulation';
import { millisecondsToTime } from '../../../utils/helpers/time';
import {
  formatYardlineWithTeam,
  getDownName,
  getToGoName,
  getYardsWon,
} from '../../../utils/helpers/play';
import { convertIntToNth } from '../../../utils/helpers/formatting';
import {
  API_PLAY_TYPES,
  API_TURNOVER_TYPES,
} from '../../../utils/constants/api';
import LoaderLabelled from '../../../components/LoaderLabelled/LoaderLabelled';
import {
  PlayByPlayCardWrap,
  OffenseCell,
  OutcomeCell,
  OutcomeDot,
  QuarterDivider,
  PlayByPlayGridWrap,
  ModeSwitcher,
  HeaderRow,
  HeaderLeftGroup,
  ViewSwitchButtonInner,
  SelectionActionBar,
  EmptyState,
} from './GameOverview.styles';

// AG Grid v34 is modular — every feature must be registered. Sparklines
// need AG Charts Enterprise (Hudl's license covers both). ColumnAutoSize
// powers the autoSizeStrategy that makes columns hug their content.
ModuleRegistry.registerModules([
  SparklinesModule.with(AgChartsEnterpriseModule),
  ColumnAutoSizeModule,
]);

// Same theme AG Grid uses by default, with the font taken from the
// surrounding page. Without this the grid loads IBM Plex Sans from Google
// Fonts instead of the app's theme font.
const driveTableTheme = themeQuartz.withParams({ fontFamily: 'inherit' });

const QUARTER_NAMES = {
  1: '1st Quarter',
  2: '2nd Quarter',
  3: '3rd Quarter',
  4: '4th Quarter',
};
const quarterLabel = (q) => QUARTER_NAMES[q] || `OT${q - 4}`;

function QuarterDividerRenderer({ data }) {
  return <QuarterDivider>{data?.quarterLabel}</QuarterDivider>;
}

function OffenseCellRenderer({ data, context }) {
  const team = data?.offenseTeam;
  if (!team) return data?.offenseName ?? '';
  return (
    <OffenseCell>
      <TeamIcon team={team} context={context} />
      {team.shortName || team.name}
    </OffenseCell>
  );
}

// Per-team score column header: team icon + short name. Used twice (away,
// home) to label the two score columns on the drive table.
function TeamScoreHeader({ team, context }) {
  if (!team) return null;
  return (
    <OffenseCell>
      <TeamIcon team={team} context={context} />
      {team.shortName || team.name}
    </OffenseCell>
  );
}

// Custom loading overlay so the initial render shows a kitbag Loader instead
// of AG Grid's default "No Rows To Show" / plain "Loading..." text.
function LoadingOverlay() {
  return <Loader />;
}

function VideoCellRenderer({ data, context }) {
  if (!data) return null;
  return (
    <Tooltip content="Watch all plays in drive" placement="top-start">
      <ButtonIcon
        icon="Play"
        variant="ghost"
        size="small"
        title="Watch all plays in drive"
        ariaLabel="Watch all plays in drive"
        onClick={(e) => {
          e.stopPropagation();
          context?.onSelectDrive?.(data);
        }}
      />
    </Tooltip>
  );
}

// Kitbag ButtonIcon chevron — mirrors the action column's styling exactly
// (ghost variant, small size, centred via the action-cell class) so the
// two end columns look identical. Subscribes to AG Grid's expandedChanged
// event so the icon flips between right and down as the row toggles.
function ChevronCellRenderer({ node }) {
  const [expanded, setExpanded] = useState(node?.expanded ?? false);
  useEffect(() => {
    if (!node) return undefined;
    const onChange = () => setExpanded(node.expanded);
    node.addEventListener('expandedChanged', onChange);
    return () => node.removeEventListener('expandedChanged', onChange);
  }, [node]);
  if (!node) return null;
  return (
    <Tooltip content={expanded ? 'Collapse drive' : 'Expand drive'}>
      <ButtonIcon
        icon={expanded ? 'ChevronDown' : 'ChevronRight'}
        variant="ghost"
        size="small"
        title={expanded ? 'Collapse drive' : 'Expand drive'}
        ariaLabel={expanded ? 'Collapse drive' : 'Expand drive'}
        onClick={(e) => {
          e.stopPropagation();
          node.setExpanded(!node.expanded);
        }}
      />
    </Tooltip>
  );
}

function PlayOutcomeCellRenderer({ data }) {
  if (!data?.outcomeText) return '-';
  return (
    <OutcomeCell>
      <OutcomeDot $colour={data.outcomeColor} />
      <span>{data.outcomeText}</span>
    </OutcomeCell>
  );
}

function DescriptionCellRenderer({ data, api }) {
  return (
    <PlayDescriptionCell play={data} api={api} autoSizeColumnId="description" />
  );
}

function PlayVideoCellRenderer({ data, context }) {
  if (!data) return null;
  return (
    <Tooltip content="Watch play" placement="top-start">
      <ButtonIcon
        icon="Play"
        variant="ghost"
        size="small"
        title="Watch play"
        ariaLabel="Watch play"
        onClick={(e) => {
          e.stopPropagation();
          context?.onSelectPlay?.(data);
        }}
      />
    </Tooltip>
  );
}

// Drive-level columns (master rows), matching the existing Play by Play table's
// drive header row. Factory so the sparkline formatter can close over the
// kitbag theme for success/error colours, and the score columns can render
// per-team headers (away on the left, home on the right).
const buildDriveColumnDefs = (theme, game) => [
  {
    headerName: '',
    colId: 'driveActions',
    cellRenderer: VideoCellRenderer,
    sortable: false,
    resizable: false,
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellClass: 'action-cell',
    headerClass: 'action-cell',
  },
  // initialWidth avoids a 200px-default flash before the autoSize pass runs.
  { headerName: '#', field: 'gameIndex', initialWidth: 56 },
  {
    headerName: 'Offense',
    field: 'offenseName',
    cellRenderer: OffenseCellRenderer,
  },
  { headerName: 'Clock', field: 'clock' },
  { headerName: 'Yardline', field: 'startingFieldPosition' },
  { headerName: 'Plays', field: 'playCount', type: 'rightAligned' },
  {
    headerName: 'Yards gained',
    field: 'yardsGainedText',
    type: 'rightAligned',
  },
  {
    headerName: '',
    field: 'yardsGainedSparkline',
    suppressAutoSize: true,
    suppressSizeToFit: true,
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    cellRenderer: 'agSparklineCellRenderer',
    cellRendererParams: {
      sparklineOptions: {
        type: 'bar',
        direction: 'horizontal',
        // The yards value is already in the "Yards gained" column, and the
        // tooltip shows it unformatted (e.g. 3.9200000000000017).
        tooltip: { enabled: false },
        // Negative min lets drives with lost yardage render leftward from
        // the zero baseline instead of being clipped off the scale.
        min: -20,
        max: 100,
        // Axis line styled to match the table's row dividers.
        axis: {
          stroke: theme.colours.canvas.secondary.strong,
          strokeWidth: 1,
        },
        // Let the plot area (and axis line) run taller; widthRatio below
        // keeps the bar half that height.
        padding: { top: 6, bottom: 6 },
        // Bar thickness as a fraction of the plot-area cross-axis — half
        // the axis line height.
        widthRatio: 0.5,
        // First slot in the kitbag data-vis palette, consistent fill for
        // every row.
        fill: theme.colours.visualisations.dataObjects.default.n1.main,
        strokeWidth: 0,
      },
    },
  },
  { headerName: 'Result', field: 'driveResult', flex: 1 },
  {
    headerComponent: TeamScoreHeader,
    headerComponentParams: { team: game?.awayTeam },
    field: 'awayScore',
    cellClass: 'centered-cell',
    headerClass: 'centered-cell',
  },
  {
    headerComponent: TeamScoreHeader,
    headerComponentParams: { team: game?.homeTeam },
    field: 'homeScore',
    cellClass: 'centered-cell',
    headerClass: 'centered-cell',
  },
  {
    headerName: '',
    colId: 'driveExpand',
    cellRenderer: ChevronCellRenderer,
    sortable: false,
    resizable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    valueFormatter: () => '',
    pinned: 'right',
    lockPinned: true,
    cellClass: 'action-cell',
    headerClass: 'action-cell',
  },
];

// Hide AG Grid's header menu UI: suppressHeaderMenuButton removes the
// three-dot icon, suppressHeaderContextMenu disables right-click on headers.
// resizable: false disables the drag-to-resize handle on all columns.
// suppressMovable: true disables drag-to-reorder on all columns.
// sortable: false disables click-to-sort on headers.
// valueFormatter renders a dash for empty values so plain-field columns
// never show a blank cell. cellRenderer columns handle their own empty
// case (e.g. PlayOutcomeCellRenderer, DescriptionCellRenderer return '-').
const defaultColDef = {
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  resizable: false,
  suppressMovable: true,
  sortable: false,
  valueFormatter: ({ value }) => (value == null || value === '' ? '-' : value),
};

const autoSizeStrategy = {
  type: 'fitCellContents',
};

// Resolve play outcome label + colour the same way the standalone Play by
// Play page does, pulling colours from the kitbag visualisations palette.
const resolveOutcome = (play, successFail) => {
  if (play.downNegated) {
    return { text: 'Negated Down', color: successFail.neutral.main };
  }
  if (play.touchdownWon) {
    return { text: 'Touchdown', color: successFail.superSuccess.main };
  }
  if (play.turnoverType) {
    return {
      text: API_TURNOVER_TYPES[play.turnoverType],
      color: successFail.superFail.main,
    };
  }
  if (play.success) {
    return { text: 'Success', color: successFail.success.main };
  }
  if (play.down && !play.success) {
    return { text: 'Fail', color: successFail.fail.main };
  }
  return { text: '', color: successFail.neutral.main };
};

const formatEpa = (epa) => {
  if (epa == null) return '';
  return epa > 0 ? `+${epa.toFixed(2)}` : epa.toFixed(2);
};

// Play-level columns (detail rows), matching the existing Play by Play table's
// per-play row inside an expanded drive.
const playColumnDefs = [
  {
    headerName: '',
    colId: 'playActions',
    cellRenderer: PlayVideoCellRenderer,
    sortable: false,
    resizable: false,
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellClass: 'action-cell',
    headerClass: 'action-cell',
  },
  { headerName: '#', field: 'playNumber' },
  { headerName: 'Clock', field: 'clockText' },
  { headerName: 'Down & Dist', field: 'downAndDistText' },
  { headerName: 'Yardline', field: 'yardlineName' },
  { headerName: 'Type', field: 'playTypeLabel' },
  {
    headerName: 'Yards gained',
    field: 'yardsGainedText',
    type: 'rightAligned',
  },
  {
    headerName: 'Play outcome',
    field: 'outcomeText',
    cellRenderer: PlayOutcomeCellRenderer,
  },
  { headerName: 'EPA', field: 'epaText', type: 'rightAligned' },
  {
    headerName: 'Description',
    colId: 'description',
    cellRenderer: DescriptionCellRenderer,
    flex: 1,
  },
];

const buildDetailCellRendererParams = (context) => ({
  detailGridOptions: {
    columnDefs: playColumnDefs,
    defaultColDef,
    autoSizeStrategy,
    suppressCellFocus: true,
    suppressContextMenu: true,
    context,
    // autoSizeStrategy doesn't reliably fire inside detail grids in
    // v34 (the detail grid is created lazily on row expand and the
    // strategy's initial-only contract races against data loading).
    // Trigger column auto-sizing explicitly once the first data is
    // rendered so columns hug their content.
    onFirstDataRendered: (params) => {
      params.api.autoSizeAllColumns();
    },
  },
  getDetailRowData: (params) => params.successCallback(params.data.plays),
});

const buildDriveRow = (drive, teamsById, successFail) => ({
  ...drive,
  offenseName: drive.team?.name ?? '',
  offenseTeam: teamsById?.[drive.team?.id] ?? drive.team ?? null,
  clock: `Q${drive.startQuarter ?? ''}, ${millisecondsToTime(
    drive.quarterClockRemaining ?? 0,
  )}`.trim(),
  driveResult: drive.name?.split('→ ')[1] ?? '',
  startingFieldPosition: formatYardlineWithTeam(
    drive.startingYards,
    drive.team,
    teamsById
      ? Object.values(teamsById).find((t) => t?.id !== drive.team?.id)
      : null,
  ),
  yardsGainedText: getYardsWon(drive.yardsNet) ?? '',
  yardsGainedSparkline: [drive.yardsNet ?? 0],
  plays: (drive.playsInDrive ?? []).map((p) => {
    const downName = getDownName(p.down);
    const yardsToGoText = getToGoName(p.yardsToGo);
    const yardlineName = formatYardlineWithTeam(
      p.yardLine,
      p.offenseTeam,
      p.defenseTeam,
    );
    const downAndDistText =
      p.down != null && p.yardsToGo != null
        ? `${convertIntToNth(p.down) || String(p.down)} and ${Math.floor(
            p.yardsToGo,
          )}`
        : '';
    const outcome = resolveOutcome(p, successFail);
    return {
      ...p,
      playNumber: p.driveIndex,
      downName,
      yardsToGoText,
      downAndDistText,
      yardlineName,
      playTypeLabel: API_PLAY_TYPES[p.type] ?? p.type,
      yardsGainedText: p.yardsNet ? getYardsWon(p.yardsNet) : '',
      outcomeText: outcome.text,
      outcomeColor: outcome.color,
      penaltyText: resolvePenaltyText(p),
      clockText: formatPlayClock(p),
      epaText: formatEpa(p.scrimmageEpa),
      name:
        p.name ??
        [downName, yardsToGoText, yardlineName].filter(Boolean).join(', '),
    };
  }),
});

const buildDriveRows = (plays, teamsById, successFail) => {
  const driveIds = [...new Set(plays.map((p) => p.drive.id))];
  return driveIds.map((id) => {
    const firstPlay = plays.find((p) => p.drive.id === id);
    const drive = formatDriveInfo(firstPlay.drive, plays);
    return buildDriveRow(drive, teamsById, successFail);
  });
};

// Interleave full-width divider rows between quarter groups. Run this AFTER
// any drive-level filtering so dividers only appear above quarters that still
// have at least one drive in them.
const interleaveQuarterDividers = (drives) => {
  const rows = [];
  let currentQuarter = null;
  drives.forEach((drive) => {
    if (drive.startQuarter !== currentQuarter) {
      currentQuarter = drive.startQuarter;
      rows.push({
        isQuarterDivider: true,
        quarter: currentQuarter,
        quarterLabel: quarterLabel(currentQuarter),
      });
    }
    rows.push(drive);
  });
  return rows;
};

const driveScored = (drive) =>
  (drive.offensePoints ?? 0) > 0 || (drive.defensePoints ?? 0) > 0;

function PlayByPlayPanel({ game, onSelectDrive, onSelectPlay }) {
  const theme = useTheme();
  const selectedTeamId = useReactiveVar(mf_Teams);
  // Watch mf_Games reactively so the component re-renders once Layout's
  // useEffect sets the game ID from the URL param. Without this, the
  // GET_GAME_PLAYS query fires on first render before mf_Games is populated
  // (gameId = 0), gets an empty result, and Apollo may not re-run it.
  // The same pattern is used for GET_GAME_OVERVIEW in GameOverview.jsx.
  const selectedGameId = useReactiveVar(mf_Games);
  const [mode, setMode] = useState('all');
  // 'plays'  = flat grid with every Play schema field as a column,
  //            powered by @hudl/uniform-web-grid. The default view.
  // 'drives' = original drive/play master-detail table.
  const [viewMode, setViewMode] = useState('plays');
  // Plays selected via the checkbox column in the grid view. Drives the
  // contextual action bar in the right-side of the card header.
  const [selectedPlays, setSelectedPlays] = useState([]);
  // Clear selection when the game changes or the user switches away from the
  // grid. The grid unmounts in both cases so it never emits an empty
  // onSelectionChanged — without this, "N plays selected" and "Watch plays"
  // persist with stale data from the previous game / view.
  useEffect(() => {
    setSelectedPlays([]);
  }, [selectedGameId, viewMode]);
  // "Scoring Drives" is a Table-only filter and the Grid always lists every
  // play, so drop the filter when the user leaves Table view. It must not be
  // tied to the game id: only the view change resets it.
  useEffect(() => {
    if (viewMode !== 'drives') setMode('all');
  }, [viewMode]);
  const {
    data: playsData,
    loading,
    error,
    refetch,
  } = useQuery(GET_GAME_PLAYS, {
    skip: !selectedGameId,
  });

  const handleWatchSelectedPlays = () => {
    if (!selectedPlays.length) return;
    onSelectDrive({
      name: `${selectedPlays.length} selected plays`,
      plays: selectedPlays,
    });
  };

  // Compute the card height from its actual top offset so the card fills
  // the viewport all the way to the bottom edge on every viewport / data
  // state. ResizeObserver on document.body catches layout above the card
  // growing/shrinking (e.g. the game score header re-flowing once game data
  // loads). The effect also re-runs when `game` changes so the very first
  // measurement after the score header populates is correct immediately —
  // without waiting for the ResizeObserver to detect the body overflow and
  // fire a second time, which previously produced a brief 8px gap.
  //
  // KitbagPageGridHolder applies a bottom margin to its first child that
  // pushes the document past the viewport. Zero it out while this panel is
  // mounted and restore on unmount so the global layout for other pages is
  // unaffected.
  const cardWrapRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(null);
  useLayoutEffect(() => {
    const el = cardWrapRef.current;
    if (!el) return undefined;

    const pageGridChild = el.closest(
      '[class*="KitbagPageGridHolder"]',
    )?.firstElementChild;
    const originalMarginBottom = pageGridChild?.style.marginBottom;
    if (pageGridChild) pageGridChild.style.marginBottom = '0px';

    const measure = () => {
      const { top } = el.getBoundingClientRect();
      setCardHeight(window.innerHeight - top - 8);
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
      if (pageGridChild) {
        pageGridChild.style.marginBottom = originalMarginBottom || '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);

  const driveColumnDefs = useMemo(
    () => buildDriveColumnDefs(theme, game),
    [theme, game],
  );

  const teamsById = useMemo(() => {
    if (!game?.homeTeam || !game?.awayTeam) return null;
    return {
      [game.homeTeam.id]: game.homeTeam,
      [game.awayTeam.id]: game.awayTeam,
    };
  }, [game]);

  // Same selected-team-centric colour logic as the page header and Play by
  // Play page: selected team = HalfMoonLeft colour, opponent = HalfMoonTop.
  const isHomeTeam = game?.homeTeam?.id === selectedTeamId;
  const panelSelectedTeam = isHomeTeam ? game?.homeTeam : game?.awayTeam;
  const panelOpponentTeam = isHomeTeam ? game?.awayTeam : game?.homeTeam;
  const panelSelectedColors = useMemo(
    () => ({
      primary: panelSelectedTeam?.colorPrimary,
      secondary: panelSelectedTeam?.colorSecondary,
      tertiary: panelSelectedTeam?.colorTertiary,
    }),
    [panelSelectedTeam],
  );
  const panelOpponentColors = useMemo(
    () => ({
      primary: panelOpponentTeam?.colorPrimary,
      secondary: panelOpponentTeam?.colorSecondary,
      tertiary: panelOpponentTeam?.colorTertiary,
    }),
    [panelOpponentTeam],
  );
  const {
    selectedTeamColor: panelSelectedColor,
    opponentTeamColor: panelOpponentColor,
  } = useDistinctTeamColors(panelSelectedColors, panelOpponentColors);

  const gridContext = useMemo(
    () => ({
      onSelectDrive,
      onSelectPlay,
      selectedTeamId,
      selectedTeamColor: panelSelectedColor,
      opponentTeamColor: panelOpponentColor,
    }),
    [
      onSelectDrive,
      onSelectPlay,
      selectedTeamId,
      panelSelectedColor,
      panelOpponentColor,
    ],
  );

  const detailCellRendererParams = useMemo(
    () => buildDetailCellRendererParams(gridContext),
    [gridContext],
  );

  const { successFail } = theme.colours.visualisations.dataObjects;

  const driveRows = useMemo(() => {
    const formatted = playsData?.plays.edges
      .map(formatPlay)
      .filter((p) => p.drive);
    if (!formatted?.length) return [];
    return buildDriveRows(formatted, teamsById, successFail);
  }, [playsData, teamsById, successFail]);

  // When the game is T1-only, blank out every derived/post-snap field before
  // the rows reach the table: yardsGainedText, yardsGainedSparkline,
  // driveResult, epaText, outcomeText and outcomeColor are set to empty
  // strings / neutral values so the drives table looks exactly like a real
  // T1 game. The table's valueFormatter renders '' as '-', so no column
  // definition changes are needed — just the data.
  const t1DataOnly = game?.t1DataOnly ?? false;
  const displayDriveRows = useMemo(() => {
    if (!t1DataOnly) return driveRows;
    return driveRows.map((drive) => ({
      ...drive,
      yardsGainedText: '',
      yardsGainedSparkline: [0],
      driveResult: '',
      plays: (drive.plays ?? []).map((p) => ({
        ...p,
        yardsGainedText: '',
        epaText: '',
        outcomeText: '',
        outcomeColor: '',
      })),
    }));
  }, [driveRows, t1DataOnly]);

  const rowData = useMemo(() => {
    const filtered =
      mode === 'scoring'
        ? displayDriveRows.filter(driveScored)
        : displayDriveRows;
    return interleaveQuarterDividers(filtered);
  }, [displayDriveRows, mode]);

  // Initial load only: once data has arrived, refetches fall through to the
  // grid's own loading overlay instead of replacing the whole card body.
  // The game id is set a moment after mount (team -> game cascade), and the
  // query is skipped until then; treat that window as loading so the card
  // doesn't flash an empty grid on every team switch or cold load.
  const showLoader = (loading && !playsData) || !selectedGameId;
  // A failed load leaves playsData undefined. Without this the grids would
  // render blank, indistinguishable from a game with no plays.
  const showError = !showLoader && !!error && !playsData;
  const showEmpty =
    !showLoader && !showError && !!playsData && !playsData.plays?.edges?.length;
  const showViews = !showLoader && !showError && !showEmpty;
  // "Scoring Drives" on a game where no drive scored would otherwise leave a
  // blank table, indistinguishable from a failed render.
  const showNoScoringDrives =
    showViews && viewMode === 'drives' && mode === 'scoring' && !rowData.length;

  return (
    <div>
      <PlayByPlayCardWrap
        ref={cardWrapRef}
        style={cardHeight != null ? { height: `${cardHeight}px` } : undefined}
      >
        <Card>
          <CardHeader>
            <HeaderRow>
              <HeaderLeftGroup>
                {viewMode === 'drives' ? (
                  <ModeSwitcher>
                    <Button
                      variant={mode === 'all' ? 'primary' : 'ghost'}
                      ariaPressed={mode === 'all'}
                      size="small"
                      onClick={() => setMode('all')}
                    >
                      All Drives
                    </Button>
                    <Button
                      variant={mode === 'scoring' ? 'primary' : 'ghost'}
                      ariaPressed={mode === 'scoring'}
                      size="small"
                      onClick={() => setMode('scoring')}
                    >
                      Scoring Drives
                    </Button>
                  </ModeSwitcher>
                ) : (
                  <Typography variant="headingMedium">All plays</Typography>
                )}
                {viewMode === 'plays' && selectedPlays.length > 0 && (
                  <SelectionActionBar>
                    <Typography variant="bodyMedium">
                      {selectedPlays.length}{' '}
                      {selectedPlays.length === 1 ? 'play' : 'plays'} selected
                    </Typography>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={handleWatchSelectedPlays}
                    >
                      <ViewSwitchButtonInner>
                        <Icon variant="Play" size="small" />
                        Watch Plays
                      </ViewSwitchButtonInner>
                    </Button>
                  </SelectionActionBar>
                )}
              </HeaderLeftGroup>
              <ButtonGroup>
                <Button
                  variant={viewMode === 'plays' ? 'primary' : 'ghost'}
                  ariaPressed={viewMode === 'plays'}
                  size="small"
                  onClick={() => setViewMode('plays')}
                >
                  <ViewSwitchButtonInner>
                    <Icon variant="TableView" size="small" />
                    Grid
                  </ViewSwitchButtonInner>
                </Button>
                <Button
                  variant={viewMode === 'drives' ? 'primary' : 'ghost'}
                  ariaPressed={viewMode === 'drives'}
                  size="small"
                  onClick={() => setViewMode('drives')}
                >
                  <ViewSwitchButtonInner>
                    <Icon variant="ListItems" size="small" />
                    Table
                  </ViewSwitchButtonInner>
                </Button>
              </ButtonGroup>
            </HeaderRow>
          </CardHeader>
          <CardBody>
            {showLoader && (
              <LoaderLabelled text="Loading plays" height="100%" />
            )}
            {showError && (
              <EmptyState data-testid="plays-error" role="alert">
                <Icon variant="Info" size="regular" />
                <Typography variant="headingLarge">
                  Unable to Load Plays
                </Typography>
                <Typography variant="bodyRegular">
                  We couldn&apos;t load the plays for this game. Check your
                  connection and try again.
                </Typography>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => refetch()}
                >
                  Try Again
                </Button>
              </EmptyState>
            )}
            {showEmpty && (
              <EmptyState data-testid="plays-empty">
                <Icon variant="Info" size="regular" />
                <Typography variant="headingLarge">
                  No Plays Available
                </Typography>
                <Typography variant="bodyRegular">
                  Plays appear here once the game has been processed. Check back
                  soon.
                </Typography>
              </EmptyState>
            )}
            {showNoScoringDrives && (
              <EmptyState data-testid="no-scoring-drives">
                <Icon variant="Info" size="regular" />
                <Typography variant="headingLarge">
                  No Scoring Drives
                </Typography>
                <Typography variant="bodyRegular">
                  No drive scored in this game. Select All Drives to see every
                  drive.
                </Typography>
              </EmptyState>
            )}
            {showViews && !showNoScoringDrives && viewMode === 'drives' && (
              <PlayByPlayGridWrap>
                <AgGridReact
                  theme={driveTableTheme}
                  masterDetail
                  detailRowAutoHeight
                  suppressCellFocus
                  suppressContextMenu
                  loading={loading}
                  loadingOverlayComponent={LoadingOverlay}
                  columnDefs={driveColumnDefs}
                  defaultColDef={defaultColDef}
                  // autoSizeStrategy intentionally omitted — v34 applies it
                  // to every column including flex ones, which would freeze
                  // Result at its natural width. The onFirstDataRendered
                  // callback below auto-sizes non-flex columns explicitly.
                  // Render off-screen columns in the DOM upfront so the
                  // explicit autoSize pass below measures every column,
                  // not just the visible ones.
                  suppressColumnVirtualisation
                  onFirstDataRendered={(p) => {
                    // Auto-size every column EXCEPT those with flex set —
                    // autoSize would set an explicit width and break flex.
                    const ids = p.api
                      .getColumns()
                      .filter((c) => !c.getColDef().flex)
                      .map((c) => c.getColId());
                    p.api.autoSizeColumns(ids, false /* skipHeader */);
                  }}
                  detailCellRendererParams={detailCellRendererParams}
                  rowData={rowData}
                  context={gridContext}
                  isFullWidthRow={({ rowNode }) =>
                    rowNode.data?.isQuarterDivider === true
                  }
                  fullWidthCellRenderer={QuarterDividerRenderer}
                  getRowClass={(params) =>
                    params.data?.isQuarterDivider
                      ? 'quarter-divider-row'
                      : undefined
                  }
                  getRowHeight={(params) =>
                    params.data?.isQuarterDivider ? 32 : undefined
                  }
                  onRowClicked={(e) => {
                    // Don't toggle expansion when the click originated on a
                    // button (e.g. the "watch all plays in drive" play
                    // button) — its own onClick handles the action.
                    if (e.event?.target?.closest('button')) return;
                    if (e.node.master) {
                      e.node.setExpanded(!e.node.expanded);
                    }
                  }}
                />
              </PlayByPlayGridWrap>
            )}
            {showViews && viewMode === 'plays' && (
              <PlaysGrid
                onSelectionChange={setSelectedPlays}
                t1DataOnly={t1DataOnly}
                teamContext={gridContext}
              />
            )}
          </CardBody>
        </Card>
      </PlayByPlayCardWrap>
    </div>
  );
}

PlayByPlayPanel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  game: PropTypes.object,
  onSelectDrive: PropTypes.func,
  onSelectPlay: PropTypes.func,
};

PlayByPlayPanel.defaultProps = {
  game: null,
  onSelectDrive: () => {},
  onSelectPlay: () => {},
};

// Cell renderers are exported so their accessibility can be unit tested
// without a real AG Grid.
export { VideoCellRenderer, ChevronCellRenderer, PlayVideoCellRenderer };

export default PlayByPlayPanel;
